import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';
import { Pick } from './ContactEditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { 1: 'Initial Contact', 2: 'Contact 2', 3: 'Contact 3', 4: 'Contact 4' };
const TITLES = { call: 'Call', email: 'Send Email', sms: 'Send Text', note: 'Add Case Note' };
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const useRecordContext = (target) => {
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    if (!target) return;
    setCtx(null);
    axios.get(`${API}/referral-tracker/records/${target.record.id}/context`).then((r) => setCtx(r.data)).catch(() => toast.error('Could not load participant details'));
  }, [target]);
  return ctx;
};

export const RowActionDialog = ({ target, onClose, onCalled }) => {
  const ctx = useRecordContext(target);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setForm({ subject: target.kind === 'note' ? `Referral ${LABEL[target.n]}` : '', body: '', date: todayIso(), template_id: null }); }, [target]);
  if (!target) return null;
  const { kind, n, record } = target;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const channel = kind === 'note' ? 'case_note' : kind;
  const templates = ctx?.templates?.[channel] || [];
  const p = ctx?.participant;

  const chooseTemplate = async (id) => {
    set('template_id')(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    try {
      const fill = async (text) => (text && ctx.case_id ? (await axios.post(`${API}/case-note-templates/populate`, { case_id: ctx.case_id, body: text })).data.populated_body : text || '');
      const [subject, body] = await Promise.all([fill(t.subject), fill(t.body)]);
      setForm((f) => ({ ...f, template_id: id, subject: subject || (kind === 'note' ? t.name : f.subject), body }));
    } catch {
      setForm((f) => ({ ...f, template_id: id, subject: t.subject || f.subject, body: t.body || '' }));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === 'call') {
        const r = await axios.post(`${API}/communications/call/initiate`, { participant_id: p.id, case_id: ctx.case_id, phone_number: p.phone, call_type: 'outbound', notes: form.body || `Referral Tracker ${LABEL[n]}` });
        toast.success(r.data.is_live_integration ? 'Call started. Your phone rings first.' : 'Call started (preview mode). Choose the outcome when you finish.');
        onCalled(record, n);
        return;
      }
      if (!form.body.trim() || (kind !== 'sms' && !form.subject.trim())) { setBusy(false); return toast.error('Please fill in the message'); }
      if (kind === 'note') {
        await axios.post(`${API}/case-notes`, { case_id: ctx.case_id, subject: form.subject, template_id: form.template_id, date_of_service: form.date, content: form.body });
        toast.success('Case note added to the participant\'s case');
      } else if (kind === 'email') {
        await axios.post(`${API}/communications/email/send`, { participant_id: p.id, case_id: ctx.case_id, recipient_email: p.email, subject: form.subject, body: form.body, attachments: [], template_id: form.template_id, is_free_form: true });
        toast.success('Email sent');
      } else {
        await axios.post(`${API}/communications/sms/send`, { participant_id: p.id, case_id: ctx.case_id, phone_number: p.phone, message: form.body, template_id: form.template_id, is_free_form: true });
        toast.success('Text sent');
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const missing = p && ((kind === 'email' && !p.email) || ((kind === 'call' || kind === 'sms') && !p.phone));
  const needsCase = kind === 'note' && ctx && !ctx.case_id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col" data-testid={`rt-action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{TITLES[kind]} · {LABEL[n]}</DialogTitle>
          <DialogDescription>{record.first_name} {record.last_name}{p ? ` · ${kind === 'email' ? p.email || 'no email' : p.phone || 'no phone'}` : ''}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {!ctx ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="space-y-4">
              {missing && <p className="text-sm text-red-600" data-testid="rt-action-missing">This participant has no {kind === 'email' ? 'email address' : 'phone number'} on file.</p>}
              {needsCase && <p className="text-sm text-red-600">No open case found for this participant.</p>}
              {kind !== 'call' && (
                <Pick label="Message Template" value={form.template_id} options={templates.map((t) => ({ value: t.id, label: t.name }))} onChange={chooseTemplate} testId="rt-action-template" />
              )}
              {kind !== 'call' && !templates.length && <p className="text-xs text-muted-foreground">No templates picked for this tab yet (Referral Tracker settings).</p>}
              {kind === 'note' && (
                <div className="space-y-1.5"><Label>Date of Service</Label><Input type="date" value={form.date} onChange={(e) => set('date')(e.target.value)} data-testid="rt-action-date" /></div>
              )}
              {(kind === 'note' || kind === 'email') && (
                <div className="space-y-1.5"><Label>Subject</Label><Input value={form.subject} onChange={(e) => set('subject')(e.target.value)} data-testid="rt-action-subject" /></div>
              )}
              <div className="space-y-1.5">
                <Label>{kind === 'call' ? 'Call Notes (optional)' : kind === 'note' ? 'Note Content' : 'Message'}</Label>
                <Textarea rows={kind === 'call' ? 3 : 8} value={form.body} onChange={(e) => set('body')(e.target.value)} data-testid="rt-action-body" />
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rt-action-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !ctx || missing || needsCase} data-testid="rt-action-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{kind === 'call' ? 'Start Call' : kind === 'note' ? 'Save Case Note' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
